import { DiscordSDK } from '@discord/embedded-app-sdk';
import { BetterFetch, BetterFetchOption } from 'better-auth/react';
import { BetterAuthClientPlugin, BetterAuthPlugin } from 'better-auth';
import { createAuthEndpoint } from 'better-auth/plugins';
import { DiscordOptions } from 'better-auth/social-providers';
import z from 'zod';
import { getAccessTokens, getBotApi, getClientApi, validateActivityInstance } from '../../discord/utils';
import {
  DiscordActivityPluginOptions,
  DiscordActivitySignInBody,
  DiscordActivitySignInBodySchema,
  OAuthScopes,
} from './discord-activity-login-plugin-types';

export const discordActivityLogin = (config: DiscordActivityPluginOptions) => {
  return {
    id: 'discord-activity',
    schema: {
      session: {
        fields: {
          type: {
            type: 'string',
            required: false,
          },
          guildId: {
            type: 'string',
            required: false,
          },
          instanceId: {
            type: 'string',
            required: false,
          },
          channelId: {
            type: 'string',
            required: false,
          },
        },
      },
    },
    endpoints: {
      getDiscordConfig: createAuthEndpoint(
        '/discord-activity/config',
        {
          method: 'GET',
        },
        async (ctx) => {
          const discordConfig = ctx.context.options.socialProviders?.discord;
          return discordConfig
            ? ctx.json({
                clientId: discordConfig.clientId,
                scope: discordConfig.scope,
              })
            : null;
        }
      ),
      discordActivitySignIn: createAuthEndpoint(
        '/discord-activity/sign-in',
        {
          method: 'POST',
        },
        async (ctx) => {
          console.log('discordActivitySignIn 0: start');
          const { code, guildId, channelId, instanceId } = DiscordActivitySignInBodySchema.parse(ctx.body);
          const botApi = getBotApi(config.discordBotToken);

          console.log('discordActivitySignIn 1: got bot api');

          const discordConfig: DiscordOptions = ctx.context.options.socialProviders?.discord as DiscordOptions;
          const {
            access_token: accessToken,
            refresh_token: refreshToken,
            expires_in,
          } = await getAccessTokens(botApi, discordConfig.clientId, discordConfig.clientSecret, code);
          const accessTokenExpiresAt = new Date(Date.now() + expires_in * 1000);

          console.log('discordActivitySignIn 2: got discord auth tokens');

          // Now act as the user
          const clientApi = getClientApi(accessToken);

          console.log('discordActivitySignIn 3: got discord client api');

          const user = await clientApi.users.getCurrent();

          console.log('discordActivitySignIn 4: got discord user info');

          let avatarUrl: string;
          if (user.avatar) {
            // User has a custom avatar. Check if it's animated.
            const format = user.avatar.startsWith('a_') ? 'gif' : 'png';
            avatarUrl = `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.${format}`;
          } else {
            // Use default avatar. Index (0-5) is determined by the user's ID.
            const defaultAvatarIndex = (BigInt(user.id) >> 22n) % 6n;
            avatarUrl = `https://cdn.discordapp.com/embed/avatars/${defaultAvatarIndex}.png`;
          }

          console.log('discordActivitySignIn 5: got discord user avatar url');

          await validateActivityInstance(
            botApi,
            clientApi,
            user.id,
            instanceId,
            guildId,
            channelId,
            config.guildMembershipCache
          );

          console.log('discordActivitySignIn 6: validated instance, guild, and channel');

          const account = await ctx.context.internalAdapter.findAccountByProviderId(user.id, 'discord');

          console.log('discordActivitySignIn 7: looked up better auth account');

          if (!account) {
            console.log('discordActivitySignIn NEW USER 0');
            const baUser = await ctx.context.internalAdapter.createUser({
              email: user.email || `${user.id}@local.discord`,
              name: user.global_name || user.username,
              image: avatarUrl,
              role: 'user',
              emailVerified: !!user.email,
            });
            if (!baUser) return ctx.json({ error: 'Failed to create user' }, { status: 500 });

            console.log('discordActivitySignIn NEW USER 1: created user');

            const newAccount = await ctx.context.internalAdapter.createAccount({
              providerId: 'discord',
              userId: baUser.id,
              accountId: user.id,
              accessToken,
              refreshToken,
              accessTokenExpiresAt,
              scope: discordConfig.scope?.join(','),
            });
            if (!newAccount) return ctx.json({ error: 'Failed to create account' }, { status: 500 });

            console.log('discordActivitySignIn NEW USER 2: created account');

            const session = await ctx.context.internalAdapter.createSession(baUser.id, ctx, false, {
              type: 'discord-activity',
              guildId,
              instanceId,
              channelId,
            });
            if (!session) return ctx.json({ error: 'Failed to create session' }, { status: 500 });

            console.log('discordActivitySignIn NEW USER 3: created session');

            // await setSessionCookie(ctx, { session, user: baUser }); // (disabled for now, returning token instead)

            return ctx.json({ ok: true, token: session.token });
          } else {
            console.log('discordActivitySignIn EXISTING USER 0');

            const baUser = await ctx.context.internalAdapter.findUserById(account.userId);
            if (!baUser) return ctx.json({ error: 'Failed to find user' }, { status: 500 });

            console.log('discordActivitySignIn EXISTING USER 1: got user');

            await ctx.context.internalAdapter.updateAccount(account.id, {
              providerId: 'discord',
              accessToken,
              refreshToken,
              accessTokenExpiresAt,
              scope: discordConfig.scope?.join(','),
            });

            console.log('discordActivitySignIn EXISTING USER 2: updated account');

            ctx.context.internalAdapter.deleteSessions(baUser.id);

            console.log('discordActivitySignIn EXISTING USER 3: deleted other sessions');

            const session = await ctx.context.internalAdapter.createSession(baUser.id, ctx, false, {
              type: 'discord-activity',
              guildId,
              instanceId,
              channelId,
            });
            if (!session) return ctx.json({ error: 'Failed to create session' }, { status: 500 });

            console.log('discordActivitySignIn EXISTING USER 4: created session');

            // await setSessionCookie(ctx, { session, user: baUser }); // (disabled for now, returning token instead)

            console.log('discordActivitySignIn EXISTING USER 5: done');

            return ctx.json({ ok: true, token: session.token });
          }
        }
      ),
    },
  } satisfies BetterAuthPlugin;
};

export const discordActivityLoginClient = () => {
  return {
    id: 'discord-activity',
    $InferServerPlugin: {} as ReturnType<typeof discordActivityLogin>,
    getActions: (fetch, store, options) => {
      return {
        discordActivitySignIn: async (args: { discordSdk: DiscordSDK }, fetchOptions?: BetterFetchOption) => {
          const { discordSdk } = args;

          // The backend serves multiple Discord apps (e.g. FanForge and the standalone
          // Mafia activity) from one deployment. The request Origin is not a reliable
          // tenant signal through Discord's activity proxy, so report the running
          // activity's own app id explicitly on every auth request.
          const tenantFetchOptions: BetterFetchOption = {
            ...fetchOptions,
            query: { ...fetchOptions?.query, discordClientId: discordSdk.clientId },
          };

          // step 1: get the discord config from the server configuration of better-auth (src/server/auth/create-auth.ts)
          const discordConfig = await getDiscordConfig(fetch, tenantFetchOptions);
          if (!discordConfig) throw new Error('Failed to get Discord config');

          // step 2: use DiscordSDK to get a code with .commands.authorize
          await discordSdk.ready();
          const code = (
            await discordSdk.commands.authorize({
              client_id: discordConfig.clientId,
              response_type: 'code',
              state: '',
              prompt: 'none',
              scope: discordConfig.scope as OAuthScopes[],
            })
          ).code;

          // step 3: send the code over to like /discord-activity/sign-in, which should create user, account, session, etc and set a session cookie
          return await postDiscordActivitySignIn(
            {
              code,
              guildId: discordSdk.guildId,
              channelId: discordSdk.channelId,
              instanceId: discordSdk.instanceId,
            },
            fetch,
            tenantFetchOptions
          );
        },
      };
    },
  } satisfies BetterAuthClientPlugin;
};

// // *** helpers ***

async function getDiscordConfig(fetch: BetterFetch, fetchOptions?: BetterFetchOption) {
  const { data, error } = await fetch('/discord-activity/config', {
    method: 'GET',
    ...fetchOptions,
    output: z
      .object({
        clientId: z.string(),
        scope: z.array(z.string()),
      })
      .nullable(),
  });
  return data;
}

async function postDiscordActivitySignIn(
  body: DiscordActivitySignInBody,
  fetch: BetterFetch,
  fetchOptions?: BetterFetchOption
) {
  const { data, error } = await fetch<{ ok: boolean; token: string }>('/discord-activity/sign-in', {
    method: 'POST',
    body,
    ...fetchOptions,
  });
  return data;
}
